"use client";

import Link from "next/link";
import { Star } from "lucide-react";
import Image from "next/image";
import { useGetCampaignTaskStatus } from "@/hooks/challenges";
import { CAMPAIGN_CONFIG } from "@/config";
import { ChallengeCampaignEventState } from "@/types/challenges.types";

export function QuestsSpotlightSection() {
  const { data: tasksData } = useGetCampaignTaskStatus(
    CAMPAIGN_CONFIG.id,
    CAMPAIGN_CONFIG.groupId
  );

  // Get first 3 enabled, uncompleted quests, or first 3 enabled if all completed
  const displayQuests = tasksData?.tasksStatus
    ?.filter((task) => task.isEnabled && task.status !== ChallengeCampaignEventState.SUCCESSFUL)
    .slice(0, 3) || tasksData?.tasksStatus?.filter((task) => task.isEnabled).slice(0, 3) || [];

  // If no quests yet, don't render
  if (!displayQuests.length) {
    return null;
  }

  return (
    <section className="relative w-full bg-swapper-light py-12 md:py-16 lg:py-20">
      <div className="w-full max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-swapper-secondary mb-4">
            Quests & Challenges
          </h2>
          <p className="text-base md:text-lg text-swapper-secondary/70 max-w-2xl mx-auto">
            Complete social tasks and game challenges to accelerate your
            progress in the Swapper ecosystem.
          </p>
        </div>

        {/* Quest Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-12">
          {displayQuests.map((quest) => (
            <Link
              key={quest.taskGuid}
              href={`/quests?taskGuid=${quest.taskGuid}`}
              className="group block"
            >
              <div className="relative overflow-hidden rounded-2xl transition-all duration-200 ease-out hover:-translate-y-1">
                {/* Task Image as Full Background */}
                <div className="relative h-48 md:h-56">
                  {quest.media?.lightModeImageUrl && (
                    <Image
                      src={quest.media.lightModeImageUrl}
                      alt={quest.title}
                      fill
                      className="object-cover transition-transform duration-200 ease-out group-hover:scale-105"
                    />
                  )}
                </div>

                {/* Card Footer */}
                <div className="bg-white p-4 md:p-6 flex items-center justify-between">
                  <h3 className="text-sm md:text-base font-bold text-swapper-secondary line-clamp-2 flex-1 mr-2">
                    {quest.title}
                  </h3>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-lg md:text-xl font-bold text-swapper-secondary">
                      {parseFloat(quest.maxPoints).toFixed(0)}
                    </span>
                    <Star className="w-5 h-5 fill-swapper-primary text-swapper-primary" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}